import React, { useState } from 'react'
import MediaGrid from './MediaGallery/MediaGrid'
import MediaModal from './MediaGallery/MediaModal'
import PhotoEditor from './PhotoEditor/PhotoEditor'
import { useMediaGallery } from '../hooks/useMediaGallery'
import { useThemeDetection } from '../hooks/useThemeDetection'
import axios from 'axios'

// Auto-detect API URL based on environment
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL
  }
  // In production, use the production API URL
  if (window.location.hostname === 'sidequesting.org' || window.location.hostname === 'www.sidequesting.org') {
    return 'https://api.sidequesting.org'
  }
  // Default to localhost for development
  return 'http://localhost:8000'
}

const API_BASE_URL = getApiBaseUrl()

const MediaGallery = ({
  eventId,
  media,
  onMediaUpdate,
  onError,
  isOwner = false,
  currentUserId,
  eventOwnerId
}) => {
  const {
    selectedMedia,
    editingDescription,
    editingName,
    newDescription,
    newName,
    loading,
    openModal,
    closeModal,
    startEditDescription,
    startEditName,
    saveDescription,
    saveName,
    deleteMedia,
    cancelEdit,
    setNewDescription,
    setNewName
  } = useMediaGallery(eventId, onMediaUpdate, onError)

  const { isClearTheme } = useThemeDetection()
  const [editingPhotoData, setEditingPhotoData] = useState(null)

  const handleEditPhoto = (mediaItem) => {
    if (!mediaItem || !mediaItem.path) {
      if (process.env.NODE_ENV === 'development') console.error('Invalid media item for editing')
      return
    }

    let imageUrl
    if (mediaItem.path.startsWith('http://') || mediaItem.path.startsWith('https://')) {
      // Already a full URL - use it directly
      imageUrl = mediaItem.path
    } else {
      // Relative path - prepend API_BASE_URL
      imageUrl = `${API_BASE_URL}${mediaItem.path}`
    }

    setEditingPhotoData({
      mediaItem,
      imageUrl
    })
  }

  const handleSaveEditedPhoto = async (editedImageData) => {
    if (!editingPhotoData) return

    try {
      const blob = await fetch(editedImageData).then(r => r.blob())
      const file = new File([blob], editingPhotoData.mediaItem.originalName, {
        type: editingPhotoData.mediaItem.mimetype
      })

      const formData = new FormData()
      formData.append('media', file)

      await axios.put(
        `${API_BASE_URL}/api/events/${eventId}/media/${editingPhotoData.mediaItem._id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true
        }
      )

      setEditingPhotoData(null)
      if (onMediaUpdate) {
        onMediaUpdate()
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error saving edited photo:', error)
      if (onError) {
        onError('Failed to save edited photo')
      }
    }
  }

  const handleCancelPhotoEdit = () => {
    setEditingPhotoData(null)
  }

  return (
    <>
      <MediaGrid media={media} onMediaClick={openModal} />

      <MediaModal
        selectedMedia={selectedMedia}
        isClearTheme={isClearTheme}
        isOwner={isOwner}
        editingName={editingName}
        editingDescription={editingDescription}
        newName={newName}
        newDescription={newDescription}
        loading={loading}
        onClose={closeModal}
        onStartEditName={(mediaItem) => startEditName(mediaItem, isOwner)}
        onStartEditDescription={(mediaItem) => startEditDescription(mediaItem, isOwner)}
        onSaveName={saveName}
        onSaveDescription={saveDescription}
        onCancelEdit={cancelEdit}
        onSetNewName={setNewName}
        onSetNewDescription={setNewDescription}
        onDeleteMedia={(mediaId) => deleteMedia(mediaId, isOwner)}
        onEditPhoto={isOwner ? handleEditPhoto : null}
      />

      {editingPhotoData && (
        <PhotoEditor
          image={editingPhotoData.imageUrl}
          onSave={handleSaveEditedPhoto}
          onCancel={handleCancelPhotoEdit}
        />
      )}
    </>
  )
}

export default MediaGallery